import React, { useState, useEffect } from "react";
import SimpleFilter from "../../components/Filter/SimpleFilter";
import ActionDialog from "../../components/Dialogs/ActionDialog";
import { fetchAPIData, generateHiddenColumns, copyToClipboard, platformPermissionOptions,createFilterOptions, createSortFieldOptions } from '../../Utils/Common';
import Table, { renderCellExpand } from '../../components/Table/Table';
import { usePersistentStorage } from "../../Utils/usePersistentStorage";
import DraggableDialog from "../../components/Dialogs/DraggableDialog";
import { FormDialog } from "../../components/FormDialog/FormDialog";
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from "@mui/material/Tooltip";
import CopyIcon from '@mui/icons-material/AddToPhotosOutlined';
import './system.scss';

const uploaderStatusSchema = require("../../json/uploaderStatusSchema.json").view;

const uiSchema = {
    "metadataFiles": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "invalidDocumentPaths": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "failedCreatedDocumentPaths": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "failedUploadedDocumentPaths": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "trkFiles": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "internalTrakingFiles": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "emails": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    },
    "jobList": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    }
};


const SystemUploaderTasksTabPanel = ({ setNotify }) => {
    const [apiData, setApiData] = useState({});
    const [rowJsonData, setRowJsonData] = useState({});
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('view');  // default to view mode
    const [reload, setReload] = useState(false);
    const [isLoading, setIsloading] = useState(false);
    const handleClose = () => {
        setOpen(false);
    };

    const [uploaderTasks, setUploaderTasks] = usePersistentStorage("uploaderTasks", []);
    const [openInstructionDialog, setOpenInstructionDialog] = useState(false);
    const filterInstructionsDialogTitle = "Reloading the Uploader Task";
    const filterInstructionsTextContent = `
    The Uploader Task tab shows the status of the processed 
    tasks in the Content Integration application since the 
    content integration process was started. 
    If the Content Integration application is reloaded, 
    the status info is cleared. 
    `;
    useEffect(() => {
        setApiData(uploaderTasks);
    }, []);

    const fieldData = [
        'connectorConfigId',
        'description',
        'metadataFiles',
        'startTime',
        'finishTime',
        'elapsedTime',
        'validDocumentCount',
        'originalDocumentCount',
        'uploadedDocumentCount',
        'failedUploadedDocumentCount',
        'invalidDocumentCount',
        'invalidDocumentPaths',
        'uploadedDocumentCountRate',
        'totalFileSize',
        'totalFileSizeRate',
        'totalFileSizeAvg'
    ];

    const sortFieldData = [
        {
            field: "connectorConfigId",
            label: "ConfigId"
        }, {
            field: "description",
            label: "Description"
        }, {
            field: "metadataFiles",
            label: "Input"
        }, {
            field: "startTime",
            label: "Start"
        }, {
            field: "finishTime",
            label: "Finish"
        }, {
            field: "elapsedTime",
            label: "Elapsed(s)"
        }, {
            field: "validDocumentCount",
            label: "Found Files"
        }, {
            field: "originalDocumentCount",
            label: "Created Metadata"
        }, {
            field: "uploadedDocumentCount",
            label: "Uploaded Files"
        }, {
            field: "failedUploadedDocumentCount",
            label: "Failed Files"
        }, {
            field: "invalidDocumentCount",
            label: "Invalid Files"
        }, {
            field: "invalidDocumentPaths",
            label: "Invalid Files Paths"
        }, {
            field: "uploadedDocumentCountRate",
            label: "Uploaded Files"
        }, {
            field: "totalFileSize",
            label: "Total (KB)"
        },
        {
            field: "totalFileSizeRate",
            label: "Rate (KB/h)"
        }, {
            field: "totalFileSizeAvg",
            label: "Avg (KB)"
        }
    ];

    const getUploadedDocRate = (params) => {
        return (params.row.uploadedDocumentCount ? (params.row.uploadedDocumentCount * 60.0 * 60 / params.row.elapsedTime * 1000).toFixed(0) : 0);
    }

    const gettotalFileSize = (params) => {
        return (params.row.totalFileSize ? (params.row.totalFileSize > 0 ? params.row.totalFileSize / 1024 : 0).toFixed(0) : 0);
    }

    const gettotalFileSizeRate = (params) => {
        return (params.row.elapsedTime ? (params.row.totalFileSize * 60.0 * 60 / params.row.elapsedTime * 1000).toFixed(0) : 0);
    }

    const gettotalFileSizeAvg = (params) => {
        return (params.row.validDocumentCount ? (params.row.totalFileSize / params.row.validDocumentCount / 1024).toFixed(2) : 0);
    }

    const getStartTime = (params) => {
        return (params.row.startTime > 0 ? new Date(params.row.startTime).toLocaleDateString("en-US") : "");
    }

    const getFinishTime = (params) => {
        return (params.row.finishTime > 0 ? new Date(params.row.finishTime).toLocaleDateString("en-US") : "");
    }

    const getElapsedTime = (params) => {
        return (params.row.elapsedTime ? Math.round(params.row.elapsedTime / 1000) : "");
    }

    const columns = [
        {
            hideable: false,
            field: 'connectorConfigId',
            headerName: 'ConfigId',
            minWidth: 320,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                return params.value.toString();
            },
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: '270px', overflow: "hidden", textOverflow: "ellipsis" }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy connector config ID" onClick={(event) => copyToClipboard(event, params.row.connectorConfigId)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            hideable: false,
            field: 'description',
            headerName: 'Description',
            renderCell: renderCellExpand,
            minWidth: 100,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
        },
        {
            hideable: false,
            field: 'metadataFiles',
            headerName: 'Input',
            minWidth: 480,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            // renderCell: renderCellExpand,
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: '430px', overflow: "hidden", textOverflow: "ellipsis" }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy metadata Files" onClick={(event) => copyToClipboard(event, params.row.metadataFiles)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            hideable: false,
            field: 'startTime',
            headerName: 'Start',
            minWidth: 120,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
            valueGetter: getStartTime,
        },
        {
            hideable: false,
            field: 'finishTime',
            headerName: 'Finish',
            minWidth: 120,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
            valueGetter: getFinishTime,
        },
        {
            hideable: false,
            field: 'elapsedTime',
            headerName: 'Elapsed(s)',
            minWidth: 200,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
            valueGetter: getElapsedTime,
        },
        {
            hideable: false,
            field: 'validDocumentCount',
            headerName: 'Found Files',
            minWidth: 135,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            hideable: false,
            field: 'originalDocumentCount',
            headerName: 'Created Metadata',
            minWidth: 186,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            hideable: false,
            field: 'uploadedDocumentCount',
            headerName: 'Uploaded Files',
            minWidth: 162,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            hideable: false,
            field: 'failedUploadedDocumentCount',
            headerName: 'Failed Files',
            minWidth: 140,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            hideable: false,
            field: 'invalidDocumentCount',
            headerName: 'Invalid Files',
            minWidth: 140,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            hideable: false,
            field: 'invalidDocumentPaths',
            headerName: 'Invalid Files Paths',
            minWidth: 200,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            hideable: false,
            field: 'uploadedDocumentCountRate',
            headerName: 'Rate (file/h)',
            minWidth: 140,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueGetter: getUploadedDocRate,
        },
        {
            hideable: false,
            field: 'totalFileSize',
            headerName: 'Total (KB)',
            minWidth: 140,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueGetter: gettotalFileSize,
        },
        {
            hideable: false,
            field: 'totalFileSizeRate',
            headerName: 'Rate (KB/h)',
            minWidth: 140,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueGetter: gettotalFileSizeRate,
        },
        {
            hideable: false,
            field: 'totalFileSizeAvg',
            headerName: 'Avg (KB)',
            minWidth: 140,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueGetter: gettotalFileSizeAvg,
        },
    ];

    const viewRowData = (param) => {

        // remove the id from table data as table data only not part of json stored
        const {
            id,
            ...JsonData
        } = param.row;

        setRowJsonData(JsonData);
        setMode('view');
        setOpen(true);
    };

    const fetchData = async (apiURL2) => {
        setIsloading(true);
        await fetchAPIData(apiURL2)
            .then((data) => {
                setApiData(data);
                setUploaderTasks(data);
                // clear the reload false now that data is loaded
                setReload(false);
                setIsloading(false);
            })
            .catch((err) => {
                console.log(err);

                let message = `Failed to load uploader tasks data \n\n ${err.response.statusText} : ${err.response.data.message}`;
                setNotify({
                    isOpen: true,
                    alertTitle: 'Request failed',
                    message: message,
                    severity: 'error'
                });

                setReload(false);
                setIsloading(false);
            });

    }

    const handleInfoDialogClose = () => {
        setOpenInstructionDialog(false);
    };

    const showFilterInstructions = (event) => {
        setOpenInstructionDialog(true);
    };

    return (
        <>
            <div id="filterContainer" className="filterBtnPanel" style={{ marginTop: '20px' }}>
                <SimpleFilter permission={platformPermissionOptions} fields={createFilterOptions(fieldData)} sortFields={createSortFieldOptions(sortFieldData)} apiURL='/uploader/task/status?' fetchData={fetchData} useCompanyId={false} reload={reload} />
                <IconButton sx={{ paddingLeft: '0px' }} aria-label="filter instructions" onClick={showFilterInstructions}>
                    <InfoIcon />
                </IconButton>
            </div>

            <div id="uploaderTasksContainer">
                <div>
                    <Table data={apiData} columns={columns} hiddenColumns={generateHiddenColumns(columns)} viewRowData={viewRowData} isLoading={isLoading} />
                </div>
            </div>

            <ActionDialog
                open={open}
                mode={mode}
                onClose={handleClose}
                title="Uploader Tasks Information"
                defaultTab='JSON'
                propertiesTabContent={
                    <FormDialog
                        formId={'uploaderTaskDialogId'}
                        readOnly={true}
                        schema={uploaderStatusSchema}
                        uiSchema={uiSchema}
                        formData={rowJsonData}
                    />
                }
                jsonDataName="Uploader Tasks Reports"
                JSONTabContent={rowJsonData}
            />

            <DraggableDialog
                open={openInstructionDialog}
                onClose={handleInfoDialogClose}
                title={filterInstructionsDialogTitle}
                content={<div style={{ whiteSpace: 'pre', fontFamily: 'Roboto', fontSize: '14px' }}>{filterInstructionsTextContent}</div>}
            />

        </>
    );
}

export default SystemUploaderTasksTabPanel;